import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Clock, AlertCircle } from 'lucide-react';
import { formatDateLocal } from '../utils/dateUtils';
import {
  BLOCKED_DATE_LEGEND,
  CalendarDayStatus,
  LIMITED_AVAILABILITY_INFO_TEXT,
  LIMITED_AVAILABILITY_INFO_TITLE,
  LIMITED_AVAILABILITY_LEGEND,
  monthHasLimitedDays,
} from '../utils/productCalendarAvailability';

interface CalendarProps {
  dayAvailability: Record<string, CalendarDayStatus>;
  onSelectDates: (startDate: Date | null, endDate: Date | null) => void;
  onMonthChange?: (month: Date) => void;
}

const Calendar: React.FC<CalendarProps> = ({
  dayAvailability,
  onSelectDates,
  onMonthChange,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  useEffect(() => {
    onMonthChange?.(currentMonth);
  }, [currentMonth, onMonthChange]);

  const monthLimitedDays = useMemo(() => {
    const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const monthStatuses: Record<string, CalendarDayStatus> = {};

    for (let d = new Date(startOfMonth); d <= endOfMonth; d.setDate(d.getDate() + 1)) {
      const dateKey = formatDateLocal(new Date(d));
      monthStatuses[dateKey] = dayAvailability[dateKey] ?? 'available';
    }

    return monthHasLimitedDays(monthStatuses);
  }, [currentMonth, dayAvailability]);

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const isDateInPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const getDayStatus = (date: Date): CalendarDayStatus => {
    const dateKey = formatDateLocal(date);
    return dayAvailability[dateKey] ?? 'available';
  };

  const isDateHardBlocked = (date: Date) => getDayStatus(date) === 'blocked';

  const isDateSelectionLocked = (date: Date) => isDateInPast(date) || isDateHardBlocked(date);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const formatSelectionDate = (date: Date) =>
    new Intl.DateTimeFormat('sv-SE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);

  const orderRange = (a: Date, b: Date) => (a <= b ? { start: a, end: b } : { start: b, end: a });

  const isSingleDaySelection =
    selectedStartDate != null &&
    selectedEndDate != null &&
    isSameDay(selectedStartDate, selectedEndDate);

  const isRangeSelection =
    selectedStartDate != null &&
    selectedEndDate != null &&
    !isSameDay(selectedStartDate, selectedEndDate);

  const isPendingSelection = selectedStartDate != null && selectedEndDate == null;

  const beginPendingSelection = (date: Date, clearParent: boolean) => {
    setSelectedStartDate(date);
    setSelectedEndDate(null);
    setHoverDate(null);
    if (clearParent) {
      onSelectDates(null, null);
    }
  };

  const selectSingleDay = (date: Date) => {
    setSelectedStartDate(date);
    setSelectedEndDate(date);
    setHoverDate(null);
    onSelectDates(date, date);
  };

  const selectRange = (start: Date, end: Date) => {
    const { start: orderedStart, end: orderedEnd } = orderRange(start, end);
    setSelectedStartDate(orderedStart);
    setSelectedEndDate(orderedEnd);
    setHoverDate(null);
    onSelectDates(orderedStart, orderedEnd);
  };

  const handleDateClick = (date: Date) => {
    if (isDateSelectionLocked(date)) return;

    if (isPendingSelection) {
      if (isSameDay(date, selectedStartDate!)) {
        selectSingleDay(date);
      } else {
        selectRange(selectedStartDate!, date);
      }
      return;
    }

    if (isSingleDaySelection && !isSameDay(date, selectedStartDate!)) {
      selectRange(selectedStartDate!, date);
      return;
    }

    if (isSingleDaySelection && isSameDay(date, selectedStartDate!)) {
      return;
    }

    beginPendingSelection(date, selectedStartDate != null && selectedEndDate != null);
  };

  const handleDateHover = (date: Date) => {
    if (isPendingSelection || isSingleDaySelection) {
      setHoverDate(date);
    }
  };

  const days = ['Mån', 'Tis', 'Ons', 'Tors', 'Fre', 'Lör', 'Sön'];
  const months = [
    'Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni',
    'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December',
  ];

  const adjustedFirstDayOfMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  const isMultiDaySelection = isRangeSelection;

  const selectionSummary = (() => {
    if (!selectedStartDate) return null;

    if (isPendingSelection) {
      return {
        kind: 'pending' as const,
        label: 'Bekräfta datum',
        detail: formatSelectionDate(selectedStartDate),
        hint: 'Klicka samma dag igen för en dag, eller välj ett annat slutdatum. Du kan också bekräfta med knappen.',
      };
    }

    if (!selectedEndDate) return null;

    if (isSameDay(selectedStartDate, selectedEndDate)) {
      return {
        kind: 'confirmed' as const,
        label: 'Valt datum',
        detail: formatSelectionDate(selectedStartDate),
        hint: null as string | null,
      };
    }
    const { start, end } = orderRange(selectedStartDate, selectedEndDate);
    return {
      kind: 'confirmed' as const,
      label: 'Valt intervall',
      detail: `${formatSelectionDate(start)} – ${formatSelectionDate(end)}`,
      hint: null as string | null,
    };
  })();

  const selectionBounds = (() => {
    if (!selectedStartDate) return null;

    if (isPendingSelection) {
      if (hoverDate && !isSameDay(hoverDate, selectedStartDate)) {
        return orderRange(selectedStartDate, hoverDate);
      }
      return null;
    }

    if (!selectedEndDate) return null;

    if (isSingleDaySelection && hoverDate && !isSameDay(hoverDate, selectedStartDate)) {
      return orderRange(selectedStartDate, hoverDate);
    }
    return orderRange(selectedStartDate, selectedEndDate);
  })();

  return (
    <div className="rounded-lg shadow-md overflow-hidden bg-gray-900 border border-gray-700">
      <div className="flex justify-between items-center bg-purple-600 text-white p-4">
        <button
          onClick={prevMonth}
          className="p-1 rounded-full hover:bg-white/20 transition-colors duration-300"
        >
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-lg font-bold">
          {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h2>
        <button
          onClick={nextMonth}
          className="p-1 rounded-full hover:bg-white/20 transition-colors duration-300"
        >
          <ChevronRight size={24} />
        </button>
      </div>

      <div className="p-4">
        <div
          className="grid grid-cols-7 gap-1"
          onMouseLeave={() => setHoverDate(null)}
        >
          {days.map((day) => (
            <div key={day} className="text-center font-medium text-gray-400 text-sm py-2">
              {day}
            </div>
          ))}

          {Array.from({ length: adjustedFirstDayOfMonth }).map((_, index) => (
            <div key={`empty-${index}`} className="h-10 text-center text-gray-300"></div>
          ))}

          {Array.from({ length: daysInMonth }).map((_, index) => {
            const date = new Date(
              currentMonth.getFullYear(),
              currentMonth.getMonth(),
              index + 1
            );
            const status = getDayStatus(date);
            const isLimited = status === 'limited';
            const isPast = isDateInPast(date);
            const isLocked = isDateSelectionLocked(date);
            const isStart = selectionBounds != null && isSameDay(date, selectionBounds.start);
            const isEnd = selectionBounds != null && isSameDay(date, selectionBounds.end);
            const isRange =
              selectionBounds != null &&
              date > selectionBounds.start &&
              date < selectionBounds.end;
            const pendingDay =
              isPendingSelection &&
              selectedStartDate != null &&
              isSameDay(date, selectedStartDate) &&
              !(hoverDate != null && !isSameDay(hoverDate, selectedStartDate));

            let dayClassName =
              'h-10 flex items-center justify-center rounded-full text-sm relative transition-all duration-150';

            if (isPast) {
              dayClassName += ' text-gray-500 cursor-not-allowed';
            } else if (isLocked) {
              dayClassName += ' bg-red-950/50 text-red-300/80 cursor-not-allowed line-through decoration-red-400/50';
            } else if (pendingDay) {
              dayClassName +=
                ' bg-transparent text-purple-100 font-semibold cursor-pointer ring-2 ring-dashed ring-purple-400/80 shadow-[0_0_0_2px_rgba(168,85,247,0.25)]';
            } else if (isStart || isEnd) {
              dayClassName +=
                ' bg-purple-500 text-white font-bold cursor-pointer ring-2 ring-white shadow-[0_0_0_2px_rgba(168,85,247,0.55)] z-10 scale-105';
            } else if (isRange) {
              dayClassName +=
                ' bg-purple-600/45 text-purple-50 font-medium cursor-pointer ring-1 ring-inset ring-purple-400/60';
            } else if (isLimited) {
              dayClassName +=
                ' bg-amber-950/40 text-amber-100/90 cursor-pointer hover:bg-amber-900/50 ring-1 ring-inset ring-amber-500/30';
            } else {
              dayClassName += ' text-gray-200 hover:bg-gray-800 cursor-pointer';
            }

            const showLimitedDot = isLimited && !isStart && !isEnd && !isRange && !pendingDay;

            return (
              <div
                key={index}
                className={dayClassName}
                onClick={() => !isLocked && handleDateClick(date)}
                onMouseEnter={() => !isLocked && handleDateHover(date)}
              >
                {index + 1}
                {showLimitedDot && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-400 rounded-full" />
                )}
              </div>
            );
          })}
        </div>

        {selectionSummary && (
          <div
            className={`mt-4 rounded-lg border px-4 py-3 ${
              selectionSummary.kind === 'pending'
                ? 'border-amber-500/50 bg-amber-950/30'
                : 'border-purple-500/40 bg-purple-950/40'
            }`}
          >
            <p
              className={`text-xs font-semibold uppercase tracking-wide ${
                selectionSummary.kind === 'pending' ? 'text-amber-300' : 'text-purple-300'
              }`}
            >
              {selectionSummary.label}
            </p>
            <p className="mt-1 text-sm font-medium text-white">{selectionSummary.detail}</p>
            {selectionSummary.hint && (
              <p className="mt-1 text-xs text-amber-100/90">{selectionSummary.hint}</p>
            )}
            {selectionSummary.kind === 'pending' && selectedStartDate && (
              <button
                type="button"
                onClick={() => selectSingleDay(selectedStartDate)}
                className="mt-3 inline-flex w-full items-center justify-center rounded-none bg-purple-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-purple-700 sm:w-auto"
              >
                Bekräfta denna dag
              </button>
            )}
          </div>
        )}
      </div>

      {isMultiDaySelection && (
        <div className="border-t border-gray-700 bg-blue-900/20 p-4">
          <div className="flex items-start">
            <Clock className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-blue-300 font-medium text-sm">Viktigt vid flerdagshyra:</p>
              <p className="text-blue-400 text-sm mt-1">
                Vid bokning över flera dagar sker återlämning senast kl. 12.00 på den sista dagen.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-gray-700 p-4">
        <div className="flex flex-wrap gap-4 text-sm text-gray-300">
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-red-100 border border-red-800 mr-2"></div>
            <span>{BLOCKED_DATE_LEGEND}</span>
          </div>
          {monthLimitedDays && (
            <div className="flex items-center">
              <div className="w-4 h-4 rounded-full bg-amber-900/40 border border-amber-500 mr-2"></div>
              <span>{LIMITED_AVAILABILITY_LEGEND}</span>
            </div>
          )}
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-purple-500 ring-2 ring-white mr-2"></div>
            <span>Vald start/slut</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-purple-600/45 ring-1 ring-purple-400/60 mr-2"></div>
            <span>Datum i intervallet</span>
          </div>
        </div>
      </div>

      {monthLimitedDays && (
        <div className="border-t border-gray-700 bg-amber-900/20 p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-amber-200 font-medium text-sm">{LIMITED_AVAILABILITY_INFO_TITLE}</p>
              <p className="text-amber-300/90 text-sm mt-1">{LIMITED_AVAILABILITY_INFO_TEXT}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
